/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _concatInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/concat";
import _toConsumableArray from "@babel/runtime-corejs3/helpers/esm/toConsumableArray";
import _sliceInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/slice";
import _Date$now from "@babel/runtime-corejs3/core-js-stable/date/now";
import _Object$getOwnPropertyDescriptor from "@babel/runtime-corejs3/core-js-stable/object/get-own-property-descriptor";
import _Object$defineProperty from "@babel/runtime-corejs3/core-js-stable/object/define-property";
import _Object$getOwnPropertyNames from "@babel/runtime-corejs3/core-js-stable/object/get-own-property-names";
import _Object$assign from "@babel/runtime-corejs3/core-js-stable/object/assign";
import _objectSpread from "@babel/runtime-corejs3/helpers/esm/objectSpread2";
import _typeof from "@babel/runtime-corejs3/helpers/esm/typeof";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _forEachInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/for-each";
import _Array$isArray from "@babel/runtime-corejs3/core-js-stable/array/is-array";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime-corejs3/helpers/esm/assertThisInitialized";
import _getPrototypeOf from "@babel/runtime-corejs3/helpers/esm/getPrototypeOf";
import _get from "@babel/runtime-corejs3/helpers/esm/get";
import _inherits from "@babel/runtime-corejs3/helpers/esm/inherits";
import _createSuper from "@babel/runtime-corejs3/helpers/esm/createSuper";

/** @module browser/classes/loader */
import { log, SEEDS, randomizer } from '@codistica/core';
import { EventEmitter } from 'eventemitter3';
import { Payload } from './payload.js';
import { Thread } from './thread.js';
/**
 * @typedef loaderPayloadPayloadDataType
 * @property {number|null} [total=null] - Payload size in bytes.
 * @property {boolean} [createObjectUrl=false] - If true, object url will be created from response. Only applies if response is of blob type.
 * @property {boolean} [force=false] - Indicates that payload usage must be forced.
 * @property {string} path - Request URL.
 * @property {string|null} [id] - Request ID.
 * @property {string} [requestMethod='GET'] - HTTP method to be used.
 * @property {(''|'arraybuffer'|'blob'|'document'|'json'|'text')} [responseType='text'] - Response type.
 * @property {boolean} [noCache=false] - If true, appends unique queries to the request URL so fresh responses are forced.
 */

/**
 * @typedef loaderOptionsType
 * @property {number} [maxThreads=4] - Maximum concurrent threads.
 * @property {number} [activeTimeThreshold=100] - Minimum measured time in milliseconds that must be accumulated before using for calculations.
 * @property {string|null} [threadIndex=null] - Thread index in Loader.
 */

/**
 * @classdesc A class for asynchronous assets download management.
 */

var Loader = /*#__PURE__*/function (_EventEmitter) {
  _inherits(Loader, _EventEmitter);

  var _super = _createSuper(Loader);

  /**
   * @description Constructor.
   * @param {loaderOptionsType} [options] - Loader options.
   */
  function Loader() {
    var _context, _context2, _context3, _context4, _context5, _context6, _context7, _context8, _context9, _context10, _context11, _context12, _context13, _context14, _context15, _context16, _context17;

    var _this;

    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    _classCallCheck(this, Loader);

    _this = _super.call(this);
    _this.threads = {};
    _this.queue = [];
    _this.history = [];
    _this.options = {
      maxThreads: 0,
      activeTimeThreshold: typeof options.activeTimeThreshold === 'number' ? options.activeTimeThreshold : 100
    };
    _this.status = {
      inProgress: 0,
      stopRequested: false
    };
    _this.stats = {
      succeeded: 0,
      failed: 0
    };
    _this.progress = {
      // TODO: WHEN/HOW TO SET LAP? (RESTART PROGRESS). AFTER HITTING 100%?
      total: 0,
      loaded: 0,
      percent: 0,
      eta: null
    };
    _this.performance = {
      rtt: null,
      rttAhead: null,
      throughput: null
    }; // CREATE INITIAL THREADS

    _this.setMaxThreads(typeof options.maxThreads === 'number' ? options.maxThreads : 4); // BIND METHODS


    _this.add = _bindInstanceProperty(_context = _this.add).call(_context, _assertThisInitialized(_this));
    _this.remove = _bindInstanceProperty(_context2 = _this.remove).call(_context2, _assertThisInitialized(_this));
    _this.getPayloads = _bindInstanceProperty(_context3 = _this.getPayloads).call(_context3, _assertThisInitialized(_this));
    _this.start = _bindInstanceProperty(_context4 = _this.start).call(_context4, _assertThisInitialized(_this));
    _this.stop = _bindInstanceProperty(_context5 = _this.stop).call(_context5, _assertThisInitialized(_this));
    _this.reset = _bindInstanceProperty(_context6 = _this.reset).call(_context6, _assertThisInitialized(_this));
    _this.callThreads = _bindInstanceProperty(_context7 = _this.callThreads).call(_context7, _assertThisInitialized(_this));
    _this.sortQueue = _bindInstanceProperty(_context8 = _this.sortQueue).call(_context8, _assertThisInitialized(_this));
    _this.setMaxThreads = _bindInstanceProperty(_context9 = _this.setMaxThreads).call(_context9, _assertThisInitialized(_this));
    _this.balanceThreads = _bindInstanceProperty(_context10 = _this.balanceThreads).call(_context10, _assertThisInitialized(_this));
    _this.refresh = _bindInstanceProperty(_context11 = _this.refresh).call(_context11, _assertThisInitialized(_this));
    _this.emit = _bindInstanceProperty(_context12 = _this.emit).call(_context12, _assertThisInitialized(_this));
    _this.onEndHandler = _bindInstanceProperty(_context13 = _this.onEndHandler).call(_context13, _assertThisInitialized(_this));
    _this.onComputableProgressHandler = _bindInstanceProperty(_context14 = _this.onComputableProgressHandler).call(_context14, _assertThisInitialized(_this));
    _this.onDeltaLoadedHandler = _bindInstanceProperty(_context15 = _this.onDeltaLoadedHandler).call(_context15, _assertThisInitialized(_this));
    _this.onDeltaTotalHandler = _bindInstanceProperty(_context16 = _this.onDeltaTotalHandler).call(_context16, _assertThisInitialized(_this));
    _this.onHeadersHandler = _bindInstanceProperty(_context17 = _this.onHeadersHandler).call(_context17, _assertThisInitialized(_this));
    return _this;
  }
  /**
   * @description Add new item to loader queue.
   * @param {(loaderPayloadPayloadDataType|Array<loaderPayloadPayloadDataType>)} item - Item to be added.
   * @param {boolean} [addNext=false] - Add to the start of the queue.
   * @returns {string|Array<string>} Added item respective payload instance.
   */


  _createClass(Loader, [{
    key: "add",
    value: function add(item, addNext) {
      var _this2 = this,
          _context18;

      var output = [];
      item =
      /** @type {Array<loaderPayloadPayloadDataType>} */
      _Array$isArray(item) ? item : [item];

      _forEachInstanceProperty(item).call(item, _bindInstanceProperty(_context18 = function _context18(payloadData) {
        var _this3 = this,
            _context19;

        _newArrowCheck(this, _this2);

        var payload = null;

        if (!payloadData.force) {
          payload = this.getPayloads(payloadData.path)[0]; // GET LAST ADDED PAYLOAD

          if (_typeof(payload) === 'object') {
            output.push(payload);
            return;
          }
        }

        payload = new Payload(_objectSpread({}, payloadData, {
          id: randomizer.getUniqueId()
        })); // ATTACH HANDLERS

        payload.once('send', _bindInstanceProperty(_context19 = function _context19(_ref) {
          _newArrowCheck(this, _this3);

          var that = _ref.that;
          that.once('end', this.onEndHandler);
          that.on('computableprogress', this.onComputableProgressHandler);
          that.on('deltaloaded', this.onDeltaLoadedHandler);
          that.on('deltatotal', this.onDeltaTotalHandler);
          that.once('headers', this.onHeadersHandler);
        }).call(_context19, this)); // SEND PAYLOAD TO QUEUE

        if (addNext) {
          this.queue.unshift(payload);
        } else {
          this.queue.push(payload);
        }

        log.debug('Loader()', "".concat(payload.requestUrl, " SENT TO QUEUE"))(); // UPDATE PROGRESS

        this.progress.total += payload.progress.total !== null ? payload.progress.total : 0; // PUSH PAYLOAD TO OUTPUT

        output.push(payload);
      }).call(_context18, this));

      if (this.status.inProgress === 0) {
        this.start();
      }

      return output.length === 1 ? output[0] : output;
    } // TODO
    // /**
    //  * @param {string|Array<string>}    item
    //  * @param {boolean} removeAll
    //  * @returns {boolean|Array<boolean>}
    //  */
    // remove(item, removeAll) {
    //     (Array.isArray(item) ? item : [item]).forEach((path) => {
    //         const payloads = this.getPayloads(path);
    //         if (payloads.length > 0) {
    //
    //         }
    //     });
    // }

    /**
     * @description Returns an array with all payloads matching indicated path.
     * @param {string} path - Payload path.
     * @returns {Array<Object<string,*>>} Payloads array.
     */

  }, {
    key: "getPayloads",
    value: function getPayloads(path) {
      var _this4 = this,
          _context20;

      var locations = [this.queue, this.threads, this.history];
      var output = [];

      _forEachInstanceProperty(locations).call(locations, _bindInstanceProperty(_context20 = function _context20(location) {
        var _this5 = this;

        _newArrowCheck(this, _this4);

        var payload = null;

        if (_Array$isArray(location)) {
          var _context21;

          _forEachInstanceProperty(location).call(location, _bindInstanceProperty(_context21 = function _context21(elem) {
            _newArrowCheck(this, _this5);

            payload = elem instanceof Payload ? elem : elem.payload;

            if (payload && payload.path === path) {
              output.push(payload);
            }
          }).call(_context21, this));
        } else {
          for (var i in location) {
            if (!Object.prototype.hasOwnProperty.call(location, i)) {
              continue;
            }

            payload = location[i] instanceof Payload ? location[i] : location[i].payload;

            if (payload && payload.path === path) {
              output.push(payload);
            }
          }
        }
      }).call(_context20, this));

      return output;
    }
  }, {
    key: "start",
    value: function start() {
      this.status.stopRequested = false;
      this.callThreads();
    }
  }, {
    key: "stop",
    value: function stop() {
      this.status.stopRequested = true;
    }
  }, {
    key: "reset",
    value: function reset() {
      // RESET INSTANCE
      _Object$assign(this, new Loader({
        maxThreads: this.options.maxThreads,
        activeTimeThreshold: this.options.activeTimeThreshold
      }));
    }
  }, {
    key: "callThreads",
    value: function callThreads() {
      var flag = true;

      while (this.status.inProgress < this.options.maxThreads && this.queue.length !== 0 && !this.status.stopRequested) {
        for (var i in this.threads) {
          if (!Object.prototype.hasOwnProperty.call(this.threads, i)) {
            continue;
          }

          if (this.threads[i].payload === null) {
            flag = false; // START REQUEST

            this.threads[i].run(this.queue.shift());
            this.status.inProgress++;
            break;
          }
        }

        if (flag) {
          log.error('Loader()', 'NO FREE THREAD WAS FOUND')();
          return;
        }
      }
    } // TODO
    // sortQueue() {
    //
    // }

    /**
     * @description Dynamically set a new maximum number of concurrent threads.
     * @param {number} newMax - New maximum.
     */

  }, {
    key: "setMaxThreads",
    value: function setMaxThreads(newMax) {
      this.options.maxThreads = newMax; // CHECK THAT MAXIMUM THREADS NUMBER LIMIT IS ALWAYS RESPECTED
      // MAXIMUM POSSIBLE THREADS NUMBER DEPEND ON BROWSER. TO IMPROVE TRACKING, AVOID BROWSER QUEUING THREADS
      // INTERNAL MAXIMUM HAS BEEN SET TO 26
      // MORE SIMULTANEOUS THREADS MAY REQUIRE MORE CPU POWER

      if (this.options.maxThreads > 26) {
        log.warning('Loader()', 'MORE THAN 26 THREADS ARE NOT ALLOWED. FORCING 26')();
        this.options.maxThreads = 26;
      }

      this.balanceThreads();
    }
  }, {
    key: "balanceThreads",
    value: function balanceThreads() {
      var currentThreads = _Object$getOwnPropertyNames(this.threads).length;

      var i = null;
      var j = null;
      var deletedCount = 0;
      var key = null;
      var correctKey = null;

      if (currentThreads > this.options.maxThreads) {
        // CASE: LESS THREADS NEEDED
        log.debug('Loader()', 'CURRENT THREADS NUMBER HIGHER THAN EXPECTED. DELETING THREADS WHEN POSSIBLE')();
        j = 0;

        for (i = 0; i < currentThreads; i++) {
          key = SEEDS.alphaUp.charAt(i);
          correctKey = SEEDS.alphaUp.charAt(j);

          if (this.threads[key].payload === null && deletedCount < currentThreads - this.options.maxThreads) {
            delete this.threads[key];
            deletedCount++;
          } else if (key !== correctKey) {
            _Object$defineProperty(this.threads, correctKey, _Object$getOwnPropertyDescriptor(this.threads, key)); // TODO: USE FiberObject.renameProperty()


            delete this.threads[key]; // UPDATE THREAD INDEX

            this.threads[correctKey].threadIndex = correctKey;
            j++;
          } else {
            j++;
          }
        }

        log.info('Loader()', "".concat(deletedCount, " THREAD LINES DELETED"))();
      } else if (currentThreads < this.options.maxThreads) {
        // CASE: MORE THREADS NEEDED
        log.debug('Loader()', 'CURRENT THREADS NUMBER LOWER THAN EXPECTED. CREATING NEW THREADS')();

        for (i = currentThreads; i < this.options.maxThreads; i++) {
          // CREATE NEW THREADS
          key = SEEDS.alphaUp.charAt(i);
          this.threads[key] = new Thread({
            activeTimeThreshold: this.options.activeTimeThreshold,
            threadIndex: key
          });
        }

        log.info('Loader()', "".concat(this.options.maxThreads - currentThreads, " NEW THREADS CREATED"))(); // CALL NEW THREADS

        log.debug('Loader()', 'CALLING NEW THREADS')();
        this.callThreads();
      }
    }
  }, {
    key: "refresh",
    value: function refresh() {
      // TODO: REDUCE REFRESH CALLS? MORE LIKE FIRST IMPLEMENTATION
      // GET PERCENT
      this.progress.percent = 100 * this.progress.loaded / this.progress.total;
      log.verbose('Loader()', "PERCENT: ".concat(this.progress.percent, " %"))(); // GET ETA

      if (this.performance.throughput !== null && this.performance.rtt !== null && this.performance.rttAhead !== null) {
        log.debug('Loader()', "TOTAL: ".concat(this.progress.total))();
        log.debug('Loader()', "LOADED: ".concat(this.progress.loaded))();
        log.debug('Loader()', "MAX THREADS: ".concat(this.options.maxThreads))();
        log.debug('Loader()', "QUEUE LENGTH: ".concat(this.queue.length))();
        this.progress.eta = (this.progress.total - this.progress.loaded) / this.performance.throughput + this.performance.rttAhead + this.performance.rtt * Math.ceil(this.queue.length / this.options.maxThreads);
      } else {
        this.progress.eta = null;
      }

      log.verbose('Loader()', this.progress.eta === null ? 'ETA: -' : "ETA: ".concat(this.progress.eta / 1000, " s"))();
    }
    /**
     * @description Callback for end event.
     * @param {{that: Object<string,*>}} arg - Event passed arguments.
     */

  }, {
    key: "onEndHandler",
    value: function onEndHandler(_ref2) {
      var that = _ref2.that;
      // TODO: REMEMBER .succeeded AND .failed (ON SUCCESS AND ON FAIL)
      // DETACH HANDLERS
      that.off('computableprogress', this.onComputableProgressHandler);
      that.off('deltaloaded', this.onDeltaLoadedHandler);
      that.off('deltatotal', this.onDeltaTotalHandler);
      this.history.push(that);
      this.status.inProgress--;
      this.balanceThreads();

      if (this.queue.length !== 0) {
        // CALL NEXT THREADS
        this.callThreads();
      } else {
        // NO ITEMS LEFT IN QUEUE
        this.emit('end');
      } // this.refresh();

    }
    /**
     * @description Callback for computableProgress event.
     * @param {{now: number}} arg - Event passed arguments.
     */

  }, {
    key: "onComputableProgressHandler",
    value: function onComputableProgressHandler(_ref3) {
      var now = _ref3.now;

      // TODO: MAKE REQUEST UPDATE A Payload METHOD?
      var currentThreads = _Object$getOwnPropertyNames(this.threads).length;

      var deltaRtt = null;
      var requestUpdateFlag = false;
      var throughput = 0;
      var rttAheadCount = 0;
      var isActiveCount = 0;
      var throughputCount = 0;
      var isUpdatedCount = 0;
      this.performance.rttAhead = 0;

      for (var i in this.threads) {
        if (!Object.prototype.hasOwnProperty.call(this.threads, i)) {
          continue;
        } // GET LATENCY AHEAD


        if (!this.threads[i].status.isActive && this.threads[i].payload !== null && this.performance.rtt !== null) {
          deltaRtt = this.performance.rtt - (now - this.threads[i].payload.stats.sendTimestamp);

          if (deltaRtt > 0) {
            this.performance.rttAhead += deltaRtt;
            rttAheadCount++;
          }
        } // GET THROUGHPUT


        if (this.threads[i].status.isActive) {
          isActiveCount++;
        } else if (this.status.inProgress < currentThreads && this.threads[i].performance.throughput !== null) {
          this.threads[i].performance.throughput = null;
          requestUpdateFlag = true; // TO NOT GET THROUGHPUT UNTIL EVERY LINE GETS ITS OWN THROUGHPUT UPDATED
        }

        if (this.threads[i].performance.throughput !== null) {
          throughput += this.threads[i].performance.throughput;
          throughputCount++;
        }
      }

      for (var _i in this.threads) {
        if (!Object.prototype.hasOwnProperty.call(this.threads, _i)) {
          continue;
        }

        if (requestUpdateFlag === true) {
          this.threads[_i].status.isUpdated = false;
        } else {
          if (this.threads[_i].status.isUpdated) {
            isUpdatedCount++;
          }
        }
      }

      if (rttAheadCount !== 0) {
        this.performance.rttAhead /= rttAheadCount;
      }

      log.verbose('Loader()', this.performance.rttAhead === null ? 'LATENCY AHEAD: -' : "LATENCY AHEAD: ".concat(this.performance.rttAhead, " ms"))();

      if (throughputCount === isActiveCount && isUpdatedCount === isActiveCount) {
        this.performance.throughput = throughput;
      }

      log.verbose('Loader()', this.performance.throughput === null ? 'THROUGHPUT: -' : "THROUGHPUT: ".concat(this.performance.throughput * 7.8125, " Kb/s"))();
      this.refresh();
    }
    /**
     * @description Callback for deltaLoaded event.
     * @param {{deltaLoaded: number}} arg - Event passed arguments.
     */

  }, {
    key: "onDeltaLoadedHandler",
    value: function onDeltaLoadedHandler(_ref4) {
      var deltaLoaded = _ref4.deltaLoaded;
      this.progress.loaded += deltaLoaded; // this.refresh();
    }
    /**
     * @description Callback for deltaTotal event.
     * @param {{deltaTotal: number}} arg - Event passed arguments.
     */

  }, {
    key: "onDeltaTotalHandler",
    value: function onDeltaTotalHandler(_ref5) {
      var deltaTotal = _ref5.deltaTotal;
      this.progress.total += deltaTotal; // this.refresh();
    }
  }, {
    key: "onHeadersHandler",
    value: function onHeadersHandler() {
      // GET LATENCY
      var x = 0;
      var rtt = null;

      for (var i in this.threads) {
        if (!Object.prototype.hasOwnProperty.call(this.threads, i) || this.threads[i].performance.rtt === null) {
          continue;
        } // DYNAMIC MEAN


        x++;
        rtt = rtt === null ? 0 : rtt;
        rtt = (rtt * (x - 1) + this.threads[i].performance.rtt) / x;
      }

      if (rtt !== null) {
        this.performance.rtt = rtt;
      }

      log.verbose('Loader()', this.performance.rtt === null ? "LATENCY: -" : "LATENCY: ".concat(this.performance.rtt, " ms"))(); // this.refresh();
    }
    /**
     * @description Event emission auxiliary method.
     * @param {(string|symbol)} event - Event name to be emitted.
     * @param {...*} args - Arguments to be passed to handlers.
     * @returns {boolean} Event emission result.
     */

  }, {
    key: "emit",
    value: function emit(event) {
      var _get2, _context22;

      var now = _Date$now();

      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      var extraProps = args[0],
          otherArgs = _sliceInstanceProperty(args).call(args, 1);

      return (_get2 = _get(_getPrototypeOf(Loader.prototype), "emit", this)).call.apply(_get2, _concatInstanceProperty(_context22 = [this, event, _objectSpread({
        that: this,
        now: now
      }, extraProps || {})]).call(_context22, _toConsumableArray(otherArgs || [])));
    }
  }]);

  return Loader;
}(EventEmitter);

export { Loader };